import React from 'react';
import { useTranslations } from 'next-intl';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { Heading } from '@app/app/_components/Heading/Heading';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { HorizontalReadOnlyField } from '@app/app/_components/HorizontalReadOnlyField/HorizontalReadOnlyField';
import { SummaryEditButton } from '@app/app/esales/transport/components/SummaryEditButton';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { PremiumType } from '@app/domain/model/eshop/transport/PremiumType';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { InformationPopover } from '@app/app/_components/InformationPopover/InformationPopover';
import { InfoCircleOutlineIcon } from '@app/app/_icons/InfoCircleOutlineIcon';
import { GhostButton } from '@app/app/_components/GhostButton/GhostButton';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';

interface Props {
    policyLabel: string;
    insuranceDuration: number;
    totalPrice: number;
    insurancePackage: string;
    cascoPackageName?: Nullable<string>;
    alarmLevel?: Nullable<string>;
    deductiblePackageCode?: Nullable<string>;
    handleOpenUpdateModal: (step: string) => void;
}

export function InsuranceConditionsPanel({
    policyLabel,
    insuranceDuration,
    totalPrice,
    insurancePackage,
    cascoPackageName,
    alarmLevel,
    deductiblePackageCode,
    handleOpenUpdateModal,
}: Props) {
    const t = useTranslations();
    const parameters = useAppParameters();

    const isCascoIncluded = insurancePackage === PremiumType.Casco || insurancePackage === PremiumType.CascoMtpl;

    const parseDeductibleCode = (code?: Nullable<string>) => {
        if (!code) return { theft: '', otherDamages: '' };

        const match = code.match(/^FR(\d+)-(\d+)$/);
        if (match) {
            const amount = match[1];
            const percentage = match[2];
            return {
                otherDamages: `${amount} Eur`,
                theft: `${percentage}%`,
            };
        }

        return { theft: '', otherDamages: '' };
    };

    const deductible = parseDeductibleCode(deductiblePackageCode);

    return (
        <CollapsablePanel
            label={policyLabel}
            collapsable={false}
            addon={
                <SummaryEditButton onClick={handleOpenUpdateModal} stepToNavigate="step-1-2">
                    {t('general.edit')}
                </SummaryEditButton>
            }>
            <div className="px-6 py-2">
                <Heading className="mb-4" level="h4">
                    {t('eshop.transport.insuranceConditions')}
                </Heading>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ReadOnlyField label={t('eshop.transport.insurancePeriodLabel')}>
                        {`${insuranceDuration} ${t('general.months').toLowerCase()}`}
                    </ReadOnlyField>
                    <ReadOnlyField label={t('eshop.transport.totalPriceLabel')}>
                        {formatEuroValue(totalPrice)}
                    </ReadOnlyField>
                </div>

                {isCascoIncluded && (
                    <div className="mt-4 flex flex-col gap-2">
                        {cascoPackageName && (
                            <HorizontalReadOnlyField label={t('eshop.transport.cascoPackageName')}>
                                {cascoPackageName}
                            </HorizontalReadOnlyField>
                        )}

                        {alarmLevel && (
                            <HorizontalReadOnlyField
                                label={
                                    <span className="flex items-center gap-0.5">
                                        {t('eshop.transport.alarmLevel')}:
                                        <GhostButton href={parameters.eshopInfoUrls.alarmLevelInfo} target="_blank">
                                            <InfoCircleOutlineIcon className="size-3 -translate-y-1 text-neutral-500" />
                                        </GhostButton>
                                    </span>
                                }
                                labelClassName="flex flex-row">
                                {alarmLevel}
                            </HorizontalReadOnlyField>
                        )}

                        {(deductible.theft || deductible.otherDamages) && (
                            <div>
                                <Paragraph isBold className="mb-2">
                                    {t('eshop.transport.deduction')}:
                                </Paragraph>
                                <div className="ml-4 flex flex-col gap-0.5">
                                    {deductible.theft && (
                                        <HorizontalReadOnlyField
                                            label={
                                                <span className="flex items-center gap-0.5">
                                                    {t('eshop.transport.theftDeductible')}:
                                                    <InformationPopover
                                                        content={
                                                            <Paragraph>
                                                                {t('eshop.transport.theftDeductibleInfo')}
                                                            </Paragraph>
                                                        }>
                                                        <InfoCircleOutlineIcon className="text-gray-500 size-3 -translate-y-1" />
                                                    </InformationPopover>
                                                </span>
                                            }>
                                            {deductible.theft}
                                        </HorizontalReadOnlyField>
                                    )}
                                    {deductible.otherDamages && (
                                        <HorizontalReadOnlyField
                                            label={
                                                <span className="flex items-center gap-0.5">
                                                    {t('eshop.transport.otherDeductible')}:
                                                    <InformationPopover
                                                        content={
                                                            <Paragraph>
                                                                {t('eshop.transport.otherDeductibleInfo')}
                                                            </Paragraph>
                                                        }>
                                                        <InfoCircleOutlineIcon className="text-gray-500 size-3 -translate-y-1" />
                                                    </InformationPopover>
                                                </span>
                                            }>
                                            {deductible.otherDamages}
                                        </HorizontalReadOnlyField>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </CollapsablePanel>
    );
}
